import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Share2, Download, Copy, Check, X, Twitter, Linkedin, 
  Mail, MessageCircle, FileText, Image, Link2, Sparkles,
  Trophy, TrendingUp, Zap, ExternalLink, Instagram
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import html2canvas from 'html2canvas';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ShareableResultCard - Beautiful shareable cards for evaluation results
 * Supports multiple formats: Image (Instagram/LinkedIn), PDF, Link
 */
const ShareableResultCard = ({ 
  isOpen, 
  onClose, 
  evaluationData,
  evaluationId,
  style = 'vibrant' // 'vibrant' or 'academic'
}) => {
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedFormat, setSelectedFormat] = useState('square'); // 'square', 'story', 'wide'
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !evaluationData || !mounted) return null;

  const {
    paper_title,
    oi_total,
    oi_novelty,
    oi_resonance,
    oi_synthesis,
    oi_ethical,
    msa_mirror_pct,
    msa_spark_pct,
    msa_assessment
  } = evaluationData;

  // Generate shareable links
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
  // Social share link uses API route for OG meta tags (crawlers see the card image)
  const socialShareLink = evaluationId 
    ? `${BACKEND_URL}/api/share/evaluation/${evaluationId}`
    : window.location.href;
  // Direct link for users to view the evaluation
  const shareableLink = evaluationId 
    ? `${window.location.origin}/evaluation/${evaluationId}`
    : window.location.href;

  // Copy link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareableLink);
      setCopied(true);
      toast({ title: "Link Copied!", description: "Share it with your peers or professors" });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({ title: "Copy Failed", description: "Please copy manually", variant: "destructive" });
    }
  };

  // Download as image
  const handleDownloadImage = async (format = 'square') => {
    if (!cardRef.current) return;
    
    setGenerating(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2,
        backgroundColor: null,
        useCORS: true,
        logging: false
      });

      const link = document.createElement('a');
      link.download = `IF-Evaluation-${evaluationId || 'result'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();

      toast({ title: "Image Downloaded!", description: "Share it on social media or with peers" });
    } catch (err) {
      console.error('Image generation error:', err);
      toast({ title: "Download Failed", variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  // Generate card image as blob for sharing
  const generateCardBlob = async () => {
    if (!cardRef.current) return null;
    try {
      const canvas = await html2canvas(cardRef.current, {
        scale: 2, backgroundColor: null, useCORS: true, logging: false
      });
      return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
    } catch { return null; }
  };

  // Share functions - use Web Share API with image when available, fallback to links
  const openShareUrl = (url) => {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const shareText = `"${paper_title?.substring(0, 60)}" scored ${oi_total?.toFixed(1)}/10 on the Originality Index (${msa_assessment || 'Evaluated'}) — via Idea Fountain`;

  const shareWithImage = async (fallbackUrl) => {
    // Try Web Share API with image first (works on mobile)
    if (navigator.share && navigator.canShare) {
      try {
        const blob = await generateCardBlob();
        if (blob) {
          const file = new File([blob], `IF-Evaluation-${evaluationId || 'result'}.png`, { type: 'image/png' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({
              title: `Originality Score: ${oi_total?.toFixed(1)}/10`,
              text: shareText,
              url: shareableLink,
              files: [file]
            });
            return;
          }
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // User cancelled
      }
    }
    // Fallback to URL-based share
    if (fallbackUrl) openShareUrl(fallbackUrl);
  };

  const shareToTwitter = () => {
    const text = `${shareText}\n\n#Research #Innovation #AcademicWriting`;
    shareWithImage(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareableLink)}`);
  };

  const shareToLinkedIn = () => {
    shareWithImage(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareableLink)}`);
  };

  const shareToWhatsApp = () => {
    const text = `Check out my research evaluation!\n\n${shareText}\n\n${shareableLink}`;
    shareWithImage(`https://wa.me/?text=${encodeURIComponent(text)}`);
  };

  const shareToInstagram = async () => {
    // Generate and download image, then open Instagram
    try {
      const blob = await generateCardBlob();
      if (blob) {
        // Download the image
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `IF-Evaluation-${evaluationId || 'result'}.png`;
        a.click();
        URL.revokeObjectURL(url);
      }
      await navigator.clipboard.writeText(`${shareText}\n\n${shareableLink}`);
      toast({ title: "Image Downloaded & Caption Copied!", description: "Open Instagram and share the downloaded image with the copied caption." });
      setTimeout(() => openShareUrl('https://www.instagram.com/'), 1000);
    } catch {
      toast({ title: "Copy the link", description: shareableLink });
    }
  };

  const shareByEmail = () => {
    const subject = `Research Evaluation: ${paper_title} — ${oi_total?.toFixed(1)}/10`;
    const body = `Hi,\n\nI wanted to share my research paper evaluation with you!\n\nPaper: ${paper_title}\nOriginality Score: ${oi_total?.toFixed(1)}/10\nAssessment: ${msa_assessment}\n\nView full evaluation: ${shareableLink}\n\nBest regards`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const downloadPDF = async () => {
    if (!evaluationId) {
      toast({ title: "Save First", description: "Please save your evaluation to download PDF", variant: "destructive" });
      return;
    }
    
    try {
      const pdfUrl = `${BACKEND_URL}/api/evaluations/${evaluationId}/pdf`;
      window.open(pdfUrl, '_blank');
      toast({ title: "PDF Opening!", description: "Your certificate will open in a new tab" });
    } catch (err) {
      toast({ title: "Download Failed", variant: "destructive" });
    }
  };

  // Score color helper
  const getScoreColor = (score) => {
    if (score >= 8) return 'text-emerald-600';
    if (score >= 6) return 'text-blue-600';
    if (score >= 4) return 'text-amber-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 8) return 'bg-emerald-100';
    if (score >= 6) return 'bg-blue-100';
    if (score >= 4) return 'bg-amber-100';
    return 'bg-red-100';
  };

  // Card dimensions based on format
  const cardDimensions = {
    square: 'w-[400px] aspect-square',
    story: 'w-[360px] aspect-[9/16]',
    wide: 'w-[600px] aspect-video'
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-bold">Share Your Results</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Preview Card */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-gray-600">Preview</span>
                <div className="flex gap-1 ml-auto">
                  {['square', 'story', 'wide'].map(fmt => (
                    <button
                      key={fmt}
                      onClick={() => setSelectedFormat(fmt)}
                      className={`px-2 py-1 text-xs rounded ${selectedFormat === fmt ? 'bg-purple-100 text-purple-700' : 'bg-gray-100'}`}
                    >
                      {fmt === 'square' ? '1:1' : fmt === 'story' ? '9:16' : '16:9'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Shareable Card Preview */}
              <div className="flex justify-center">
                <div 
                  ref={cardRef}
                  className={`${cardDimensions[selectedFormat]} ${style === 'vibrant' 
                    ? 'bg-gradient-to-br from-purple-600 via-pink-500 to-orange-500' 
                    : 'bg-gradient-to-br from-slate-800 via-slate-700 to-slate-600'
                  } rounded-xl p-6 text-white relative overflow-hidden`}
                >
                  {/* Decorative elements */}
                  <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                  
                  {/* Content */}
                  <div className="relative z-10 h-full flex flex-col justify-between">
                    {/* Top: Branding */}
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      <span className="font-medium text-sm opacity-90">Idea Fountain</span>
                    </div>

                    {/* Middle: Score */}
                    <div className="text-center py-4">
                      <div className="text-xs uppercase tracking-wider opacity-80 mb-2">Originality Index</div>
                      <div className="text-6xl font-bold mb-2">{oi_total?.toFixed(1)}</div>
                      <div className="text-lg opacity-90">out of 10</div>
                      
                      {/* Mini scores */}
                      <div className="flex justify-center gap-4 mt-4 text-xs">
                        <div className="text-center">
                          <div className="opacity-70">Novelty</div>
                          <div className="font-bold">{oi_novelty?.toFixed(1)}</div>
                        </div>
                        <div className="text-center">
                          <div className="opacity-70">Resonance</div>
                          <div className="font-bold">{oi_resonance?.toFixed(1)}</div>
                        </div>
                        <div className="text-center">
                          <div className="opacity-70">Synthesis</div>
                          <div className="font-bold">{oi_synthesis?.toFixed(1)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Bottom: Assessment */}
                    <div>
                      <div className="bg-white/20 rounded-lg px-3 py-2 backdrop-blur-sm">
                        <div className="text-xs opacity-80 mb-1">Assessment</div>
                        <div className="font-semibold text-sm truncate">
                          {msa_assessment || 'Innovation Evaluated'}
                        </div>
                      </div>
                      
                      {selectedFormat !== 'story' && paper_title && (
                        <p className="text-xs opacity-70 mt-2 line-clamp-2">
                          &ldquo;{paper_title.substring(0, 60)}{paper_title.length > 60 ? '...' : ''}&rdquo;
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Image Button */}
              <Button
                onClick={() => handleDownloadImage(selectedFormat)}
                disabled={generating}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600"
              >
                {generating ? (
                  <span className="animate-pulse">Generating...</span>
                ) : (
                  <>
                    <Image className="w-4 h-4 mr-2" />
                    Download as Image
                  </>
                )}
              </Button>
            </div>

            {/* Share Options */}
            <div className="space-y-4">
              {/* Copy Link */}
              <div className="p-4 bg-gray-50 rounded-xl">
                <div className="text-sm font-medium text-gray-600 mb-2">Shareable Link</div>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={shareableLink} 
                    readOnly 
                    className="flex-1 px-3 py-2 text-sm bg-white border rounded-lg truncate"
                  />
                  <Button onClick={handleCopyLink} variant="outline" size="sm">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              {/* Social Sharing */}
              <div className="space-y-3">
                <div className="text-sm font-medium text-gray-600">Share On</div>
                
                <div className="grid grid-cols-2 gap-2">
                  <a 
                    href={`https://wa.me/?text=${encodeURIComponent(`Check out my research evaluation!\n\n"${paper_title}"\nOriginality Score: ${oi_total?.toFixed(1)}/10\n\n${socialShareLink}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-start px-4 py-2 text-sm font-medium rounded-md border text-green-600 border-green-200 hover:bg-green-50 transition-colors"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp
                  </a>
                  
                  <a 
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`"${paper_title?.substring(0, 50)}" scored ${oi_total?.toFixed(1)}/10 on the Originality Index! #Research #Innovation`)}&url=${encodeURIComponent(socialShareLink)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-start px-4 py-2 text-sm font-medium rounded-md border text-blue-500 border-blue-200 hover:bg-blue-50 transition-colors"
                  >
                    <Twitter className="w-4 h-4 mr-2" />
                    Twitter/X
                  </a>
                  
                  <a 
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(socialShareLink)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-start px-4 py-2 text-sm font-medium rounded-md border text-blue-700 border-blue-200 hover:bg-blue-50 transition-colors"
                  >
                    <Linkedin className="w-4 h-4 mr-2" />
                    LinkedIn
                  </a>
                  
                  <Button 
                    onClick={shareToInstagram} 
                    variant="outline"
                    className="justify-start text-pink-600 border-pink-200 hover:bg-pink-50"
                  >
                    <Instagram className="w-4 h-4 mr-2" />
                    Instagram
                  </Button>
                  
                  <a 
                    href={`mailto:?subject=${encodeURIComponent(`Research Evaluation: ${paper_title}`)}&body=${encodeURIComponent(`Paper: ${paper_title}\nOriginality Score: ${oi_total?.toFixed(1)}/10\nAssessment: ${msa_assessment}\n\nView: ${shareableLink}`)}`}
                    className="inline-flex items-center justify-start px-4 py-2 text-sm font-medium rounded-md border text-gray-600 border-gray-200 hover:bg-gray-50 transition-colors col-span-2"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email
                  </a>
                </div>
              </div>

              {/* PDF Download */}
              <div className="pt-4 border-t">
                <div className="text-sm font-medium text-gray-600 mb-3">Professional Report</div>
                <Button 
                  onClick={downloadPDF} 
                  variant="outline" 
                  className="w-full justify-start"
                  disabled={!evaluationId}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF Certificate
                  {!evaluationId && <span className="ml-auto text-xs text-amber-600">(Save first)</span>}
                </Button>
              </div>

              {/* Style Toggle */}
              <div className="pt-4 border-t">
                <div className="text-sm font-medium text-gray-600 mb-3">Card Style</div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {}}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${style === 'vibrant' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}
                  >
                    <div className="w-full h-8 rounded bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 mb-2" />
                    <span className="text-xs">Vibrant</span>
                  </button>
                  <button
                    onClick={() => {}}
                    className={`flex-1 p-3 rounded-lg border-2 transition-all ${style === 'academic' ? 'border-purple-500 bg-purple-50' : 'border-gray-200'}`}
                  >
                    <div className="w-full h-8 rounded bg-gradient-to-r from-slate-700 to-slate-500 mb-2" />
                    <span className="text-xs">Academic</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Use createPortal to render modal at document.body level for proper z-index
  return createPortal(modalContent, document.body);
};

export default ShareableResultCard;
